<template>
  <jz-card :class="['assignment-card', statusClass]" @click="openAssignmentDetailsView">
    <jz-container :class="['status', statusClass]" flex>
      <jz-container :class="['label', statusClass]">
        <img :src="statusIcon" />
        {{ statusLabel }}
      </jz-container>

      <jz-container :class="['date', statusClass]">
        {{ statusText }}
        {{ statusDates }}
      </jz-container>
    </jz-container>

    <div class="title">{{ assignment.title }}</div>

    <div class="group">
      <img :src="groupIcon" />
      <div class="name">
        {{ assignment.group.name }}
      </div>
    </div>

    <jz-container class="exercise-count" flex>
      <img v-if="!assignment.submitted && assignment.status !== 1" :src="pencilIcon" />
      <div>
        {{ assignment.numFinishedExercises }}/{{ assignment.numExercises }}
        {{ "assignment.exercises" | __ }}
      </div>
    </jz-container>
  </jz-card>
</template>

<script>
import { ROUTES } from "@tt-app/constants";
import alertIcon from "@joyz/app-core/assets/icons/white/Alert.svg";
import checkCircleIcon from "@joyz/app-core/assets/icons/white/CheckCircle.svg";
import groupIcon from "@joyz/app-core/assets/icons/transparent/Group.svg";
import hourglassIcon from "@joyz/app-core/assets/icons/white/Hourglass.svg";
import pencilIcon from "@joyz/app-core/assets/icons/transparent/Pencil.svg";
import rocketIcon from "@joyz/app-core/assets/icons/white/Rocket.svg";

export default {
  name: "AssignmentCard",
  props: {
    assignment: { type: Object, required: true }
  },
  computed: {
    statusClass() {
      const classes = {
        1: "before-start-date",
        2: "after-start-before-due-not-submitted",
        3: "after-start-before-due-submitted",
        4: "after-due-not-submitted",
        5: "after-due-submitted-before-due",
        6: "after-due-submitted-after-due"
      };
      return classes[this.assignment.status];
    },
    statusDates() {
      if ([3, 5, 6].includes(this.assignment.status)) {
        const submittedDate = new Date(this.assignment.submitted);
        const month = submittedDate.getMonth() + 1;
        const day = submittedDate.getDate();
        return `${month}/${day}`;
      } else {
        const startDate = new Date(this.assignment.startDate);
        const startMonth = startDate.getMonth() + 1;
        const startDay = startDate.getDate();
        const dueDate = new Date(this.assignment.dueDate);
        const dueMonth = dueDate.getMonth() + 1;
        const dueDay = dueDate.getDate();
        return `${startMonth}/${startDay} ~ ${dueMonth}/${dueDay}`;
      }
    },
    statusIcon() {
      const icons = {
        1: rocketIcon,
        2: hourglassIcon,
        3: checkCircleIcon,
        4: alertIcon,
        5: checkCircleIcon,
        6: checkCircleIcon
      };
      return icons[this.assignment.status];
    },
    statusLabel() {
      return this.$__(`assignment.status.${this.assignment.status}`);
    },
    statusText() {
      let text = "";
      if ([3, 5, 6].includes(this.assignment.status)) {
        text = this.$__("assignment.card.date");
      }
      return text;
    }
  },
  created() {
    this.groupIcon = groupIcon;
    this.pencilIcon = pencilIcon;
  },
  methods: {
    openAssignmentDetailsView() {
      if (this.assignment.status === 1) return;

      this.$router.push({
        name: ROUTES.ASSIGNMENT.DETAILS.NAME,
        params: { assignmentId: this.assignment.id }
      });
    }
  }
};
</script>

<style lang="scss">
.jz-card.assignment-card {
  display: grid;
  gap: $gutter / 2;
  grid-template-columns: 1fr minmax(auto, 121px);
  grid-template-rows: 25px auto 17px;
  grid-template-areas: "status status" "title title" "group exercise-count";
  padding: ($gutter * 0.75) $gutter;

  &:hover {
    cursor: pointer;
  }

  &.before-start-date {
    opacity: 0.4;

    &:hover {
      cursor: default;
    }
  }

  .status {
    align-items: center;
    display: flex;
    font-size: $font-size-xs;
    grid-area: status;

    .label {
      align-items: center;
      border-radius: 4px 0 0 4px;
      color: var(--color-white);
      display: flex;
      padding: ($gutter / 4) ($gutter / 2);

      img {
        height: $icon-size-xs;
        margin: 0 4px 0 0;
      }

      &.before-start-date {
        background-color: var(--color-black-background-light);
      }

      &.after-start-before-due-not-submitted {
        background-color: var(--color-info);
      }

      &.after-start-before-due-submitted {
        background-color: var(--color-success);
      }

      &.after-due-not-submitted {
        background-color: var(--color-danger);
      }

      &.after-due-submitted-before-due {
        background-color: var(--color-success);
      }

      &.after-due-submitted-after-due {
        background-color: var(--color-success);
      }
    }

    .date {
      border-radius: 0 4px 4px 0;
      color: var(--color-dark);
      padding: ($gutter / 4) ($gutter / 2);

      &.before-start-date {
        background-color: rgba(var(--rgb-black), 0.08);
      }

      &.after-start-before-due-not-submitted {
        background-color: var(--color-info-background-light);
      }

      &.after-start-before-due-submitted {
        background-color: var(--color-success-background-light);
      }

      &.after-due-not-submitted {
        background-color: var(--color-danger-background-light);
      }

      &.after-due-submitted-before-due {
        background-color: var(--color-success-background-light);
      }

      &.after-due-submitted-after-due {
        background-color: var(--color-success-background-light);
        color: var(--color-danger);
      }
    }
  }

  .title {
    color: var(--color-dark);
    display: -webkit-box;
    font-size: $font-size-sm;
    font-weight: bold;
    grid-area: title;
    min-height: 38px;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .group {
    align-items: center;
    color: var(--color-secondary);
    display: grid;
    font-size: $font-size-xs;
    font-weight: bold;
    grid-area: group;
    grid-template-columns: 24px minmax(0, auto);
    grid-template-rows: auto;
    min-width: 0;

    img {
      height: $icon-size-xs;
      margin: 0 4px 0 0;
    }

    .name {
      word-break: keep-all;

      @include ellipsis;
    }
  }

  .exercise-count {
    grid-area: exercise-count;
    justify-content: flex-end;
    align-items: center;
    color: var(--color-secondary);
    font-size: $font-size-xs;
    font-weight: bold;

    img {
      height: $icon-size-xs;
      margin: 0 4px 0 0;
    }
  }
}
</style>
